import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Leaf } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/', label: 'Inicio' },
    { to: '/nosotros', label: 'Nosotros' },
/*     { to: '/servicios', label: 'Funciones' }, 
    { to: '/testimonios', label: 'Testimonios' }, */
    { to: '/contacto', label: 'Contacto' }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-stone-200/50 py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* 🔹 Logo + Texto */}
          <motion.div
            className="flex items-center gap-2 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link to="/" className="flex items-center gap-2">
              {/* Si tienes el logo, descomenta la línea de abajo y ajusta la ruta */}
              {/* <img src="/images/logo22.png" alt="Mi DXN Logo" className="h-9 w-auto" /> */}
              
              {/* Fallback visual con icono si la imagen no carga */}
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-600/20">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              
              <div className="flex flex-col">
                <span className="text-xl font-bold bg-gradient-to-r from-emerald-700 to-teal-600 bg-clip-text text-transparent leading-tight">
                  Mi DXN
                </span>
                <span className="text-[10px] font-medium text-stone-500 tracking-wider uppercase leading-tight">
                  Plataforma Digital
                </span>
              </div>
            </Link>
          </motion.div>

          {/* 🔹 Navegación Desktop */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.to;
              return (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.to}
                    className={`relative px-4 py-2 text-sm font-semibold transition-colors duration-300 rounded-full ${
                      isActive 
                        ? 'text-emerald-700 bg-emerald-50' 
                        : 'text-stone-600 hover:text-emerald-600 hover:bg-stone-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              );
            })}
            
            {/* Botón CTA en Desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="ml-4"
            >
              <Link
                to="/contacto"
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg shadow-emerald-600/20 transition-all hover:shadow-xl hover:scale-105 active:scale-95"
              >
                Acceder a la App
              </Link>
            </motion.div>
          </nav>

          {/* 🔹 Botón Menú Móvil */}
          <motion.button
            className="md:hidden p-2 rounded-xl text-stone-600 hover:bg-stone-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>

        {/* 🔹 Menú Móvil con AnimatePresence para animación suave */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-stone-200 rounded-b-2xl shadow-xl"
            >
              <nav className="flex flex-col py-4 px-4 space-y-1">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.to;
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={item.to}
                        className={`block px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                            : 'text-stone-600 hover:bg-stone-50 hover:text-emerald-600'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  );
                })}
                
                {/* Botón CTA en Móvil */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="pt-4 mt-2 border-t border-stone-100"
                >
                  <Link
                    to="/contacto"
                    className="block w-full text-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3.5 rounded-xl font-semibold shadow-lg shadow-emerald-600/20 active:scale-95 transition-transform"
                    onClick={() => setIsOpen(false)}
                  >
                    Acceder a la App
                  </Link>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;